// 1099s — docs/MONEY_TAXES.md Phase 4.
//
// Owner, 2026-09-26: "another section for admin to generate 1099s for
// employees". The one correction the research made to that sentence is the
// one this screen is built around: an EMPLOYEE gets a W-2, never a 1099. So a
// worker is listed for a 1099 only once someone has recorded them as a
// contractor; a worker nobody has decided about is "decide first".
//
//   GET    /api/office/1099s?year=                 workers.read_sensitive
//   PUT    /api/office/1099s/workers/:id           + financials.export (tax details)
//   POST   /api/office/1099s/filings               + financials.export
//   PUT    /api/office/1099s/filings/:id           (dates, confirmation)
//   DELETE /api/office/1099s/filings/:id           (a record made by mistake)
//
// Every figure is the server's, in CENTS (server/utils/form1099.js). A 1099
// is recorded AS FILED; pay recorded afterwards shows as a difference.
//
// Box 1 and the threshold are COMPENSATION — gross less reimbursements — since
// 2026-09-27 (the owner: "do what makes the most sense and what's industry
// standard"). A reimbursement repays a receipt; it is not pay for the work.
// The gross and the reimbursements stay on screen so the difference is seen.
import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DataTable, FwButton, FwField, FwInput, FwSelect, FwTextarea, Notice, Pill, SectionLabel, Sheet, Stat } from '../../../ui/fw';
import { useCan } from '../../../hooks/usePermission';
import { get, post, put, del } from '../../shared/http';
import { fmtDate, todayIso } from '../shared/format';
import { useAction, FormError, FormRow, SheetFooter, downloadText, csvLine } from '../records/OfficePage';
import { PageHeader, Refused, LoadFailed, Loading } from '../shared/Page';
import { money, dollarsToCents } from './SalesTax';
import './Form1099s.css';

/** What each missing item means, said to the person who has to go and get it. */
export const MISSING = {
  classification: 'Contractor or employee not decided',
  tin_type: 'SSN or EIN not recorded',
  tin: 'Taxpayer ID not on file',
  address: 'Mailing address incomplete',
  w9: 'No W-9 recorded',
};

/** Where a contractor's 1099 stands, as one pill. */
export function statusOf(row) {
  if (row.filed && row.filed.filed_on) return { key: 'filed', label: 'Filed', tone: 'ok' };
  if (row.filed) return { key: 'sent', label: 'Statement sent', tone: 'sched' };
  if (!row.over_threshold) return { key: 'under', label: 'Under threshold', tone: 'neutral' };
  return row.missing.length ? { key: 'blocked', label: 'Needs details', tone: 'warn' } : { key: 'ready', label: 'Ready', tone: 'now' };
}

/** The contractors, as the CSV an accountant is sent. No TIN is in it — the app never exports one. */
export function necCsv(s) {
  const rows = [
    [`1099-NEC, payments made in ${s.year}`, `threshold ${money(s.threshold.cents)}`, `due ${s.due}`],
    [],
    ['Worker', 'Business name', 'Paid (gross)', 'Of which reimbursements', 'Compensation (box 1)', 'Over threshold', 'TIN type', 'TIN on file', 'Address on file', 'W-9 received', 'Box 1 as filed', 'Statement sent', 'Filed', 'Missing'],
  ];
  for (const c of s.contractors) {
    rows.push([
      c.name, c.business_name || '', (c.gross / 100).toFixed(2), (c.reimbursements / 100).toFixed(2), (c.compensation / 100).toFixed(2), c.over_threshold ? 'yes' : 'no',
      c.tin_type || '', c.tin_on_file ? 'yes' : 'no', c.address_on_file ? 'yes' : 'no', c.w9_received_on || '',
      c.filed ? (c.filed.amount_cents / 100).toFixed(2) : '', c.filed ? c.filed.statement_sent_on || '' : '', c.filed ? c.filed.filed_on || '' : '',
      c.missing.map((m) => MISSING[m] || m).join('; '),
    ]);
  }
  return rows.map((r) => csvLine(r)).join('\n');
}

export default function Form1099s() {
  const canRead = useCan('workers', 'read_sensitive');
  // financials.export both records a 1099 and exports the CSV.
  const canFile = useCan('financials', 'export');
  // A worker's tax details too, since 2026-09-27 (the owner: "admin and
  // accountant can see numbers and taxes"). They were workers.update — admin
  // alone — so the bookkeeper could record a 1099 and not the W-9 behind it.
  const canEditTax = canFile;
  const [params, setParams] = useSearchParams();
  const thisYear = Number(todayIso().slice(0, 4));
  const raw = Number(params.get('year'));
  // In January everybody means LAST year's 1099s, and they are due by Jan 31.
  const year = Number.isInteger(raw) && raw >= 2000 && raw <= thisYear ? raw : thisYear - 1;
  const [openId, setOpenId] = useState(null);

  const q = useQuery({
    queryKey: ['office', '1099s', year],
    queryFn: () => get(`/office/1099s?year=${year}`),
    enabled: canRead,
  });
  const s = q.data;
  const all = s ? [...s.contractors, ...s.undecided] : [];
  const open = openId != null ? all.find((r) => r.user_id === openId) : null;

  const columns = useMemo(() => [
    { key: 'name', label: 'Worker', role: 'title', render: (r) => <strong>{r.name}</strong> },
    { key: 'business', label: 'Business name', role: 'subtitle', render: (r) => r.business_name || '—' },
    { key: 'compensation', label: 'Compensation', align: 'right', role: 'fact', render: (r) => money(r.compensation) },
    { key: 'missing', label: 'Missing', role: 'fact', render: (r) => (r.missing.length ? r.missing.map((m) => MISSING[m] || m).join(', ') : 'Nothing') },
    { key: 'status', label: 'Status', role: 'badge', render: (r) => { const st = statusOf(r); return <Pill tone={st.tone} data-status={st.key}>{st.label}</Pill>; } },
  ], []);
  const undecidedColumns = useMemo(() => [
    { key: 'name', label: 'Worker', role: 'title', render: (r) => <strong>{r.name}</strong> },
    { key: 'compensation', label: 'Compensation', align: 'right', role: 'fact', render: (r) => money(r.compensation) },
    { key: 'status', label: 'Status', role: 'badge', render: () => <Pill tone="warn" data-status="undecided">Decide first</Pill> },
  ], []);

  if (!canRead) return <Refused what="1099s" />;

  const years = [thisYear, thisYear - 1, thisYear - 2, thisYear - 3].filter((y) => y >= 2000);
  // Pay marked paid after a 1099 was recorded — NOT compensation − box 1,
  // which can differ on purpose and would say this forever.
  const changed = s ? s.contractors.filter((c) => c.filed && c.paid_since_filing > 0) : [];
  // Paid the threshold or more in total and under it once reimbursements are
  // left out. Listed as under — so named here, because an unissued 1099 is a
  // penalty found a year later, and "Under threshold" alone does not say why
  // (review of #720).
  const underByReimb = s ? s.contractors.filter((c) => c.under_by_reimbursements) : [];

  return (
    <div className="of-1099" data-screen="office-Form1099s">
      <PageHeader
        title="1099s"
        meta={s ? `Payments made in ${year} · threshold ${money(s.threshold.cents)} · due ${fmtDate(s.due)}` : String(year)}
        actions={canFile && s ? <FwButton variant="ghost" icon="download" onClick={() => downloadText(necCsv(s), `1099-nec-${year}.csv`)} data-action="export-1099s">Export CSV</FwButton> : null}
      />
      <div className="of-filters">
        <FwField label="Payments made in" htmlFor="of-1099-year">
          <FwSelect id="of-1099-year" value={String(year)} onChange={(e) => { const next = new URLSearchParams(params); next.set('year', e.target.value); setParams(next, { replace: true }); setOpenId(null); }}>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </FwSelect>
        </FwField>
      </div>
      {q.isPending ? <Loading lines={6} /> : null}
      {q.isError ? <LoadFailed error={q.error} onRetry={() => q.refetch()} what="1099s" /> : null}

      {s ? (
        <>
          <Notice tone="info" data-note="employees">
            A 1099 is for contractors only. Anyone recorded as an employee gets a W-2 instead and is not listed here — and whether someone is a contractor is a legal test, not a preference: confirm it with the accountant.
          </Notice>
          {s.threshold.assumed ? (
            <Notice tone="warn" data-note="threshold">{`The ${year} threshold is assumed to be ${money(s.threshold.cents)}; after 2026 it is adjusted for inflation. Confirm it with the accountant before filing — an admin can override it with the nec_threshold_${year} setting, which the Settings screen does not show yet.`}</Notice>
          ) : null}
          {s.undecided.length ? (
            <Notice tone="warn" data-note="undecided">{`${s.undecided.length} worker${s.undecided.length === 1 ? ' was' : 's were'} paid in ${year} and ${s.undecided.length === 1 ? 'is' : 'are'} not recorded as a contractor or an employee. Decide first — they are listed below.`}</Notice>
          ) : null}
          {s.undated && s.undated.count ? (
            <Notice tone="warn" data-note="undated">{`${s.undated.count} paystub${s.undated.count === 1 ? ' is' : 's are'} marked paid with no paid date (${money(s.undated.gross)}, ${s.undated.names.join(', ')}), so ${s.undated.count === 1 ? 'it is' : 'they are'} in no year's total. The totals here are short by that until each has a date.`}</Notice>
          ) : null}
          {underByReimb.length ? (
            <Notice tone="warn" data-note="under-by-reimbursements">{`${underByReimb.map((c) => `${c.name} (${money(c.gross)} paid, ${money(c.compensation)} for the work)`).join(', ')} ${underByReimb.length === 1 ? 'was' : 'were'} paid ${money(s.threshold.cents)} or more in ${year} but under it once reimbursements are left out, so ${underByReimb.length === 1 ? 'is' : 'are'} listed as under the threshold. That is right only if those reimbursements were backed by receipts — confirm with the accountant, and record a 1099 anyway if they were not.`}</Notice>
          ) : null}
          {changed.length ? (
            <Notice tone="warn" data-note="changed">{`Pay recorded after a 1099 was recorded: ${changed.map((c) => c.name).join(', ')}. A corrected 1099 may be needed.`}</Notice>
          ) : null}

          <div className="of-stats__row">
            <Stat data-stat="needed" label="1099s needed" value={String(s.needed)} hint={`Contractors paid ${money(s.threshold.cents)} or more for their work in ${year} — reimbursements left out`} />
            <Stat data-stat="ready" label="Ready to prepare" value={String(s.ready)} tone={s.ready < s.needed ? 'warn' : 'ok'}
              hint={s.ready < s.needed ? `${s.needed - s.ready} still missing details` : 'Nothing missing'} />
            <Stat data-stat="due" label="Due" value={fmtDate(s.due)} hint="To the contractor and to the IRS" />
          </div>

          <section className="of-1099__section" data-section="contractors">
            <SectionLabel>Contractors</SectionLabel>
            <DataTable columns={columns} rows={s.contractors} rowKey={(r) => r.user_id} caption={`Contractors paid in ${year}`}
              onRowClick={(r) => setOpenId(r.user_id)} empty={`No worker recorded as a contractor was paid in ${year}.`} />
          </section>

          {s.undecided.length ? (
            <section className="of-1099__section" data-section="undecided">
              <SectionLabel>Decide first</SectionLabel>
              <DataTable columns={undecidedColumns} rows={s.undecided} rowKey={(r) => r.user_id} caption={`Paid in ${year}, not yet classified`}
                onRowClick={(r) => setOpenId(r.user_id)} />
            </section>
          ) : null}

          {s.employees.length ? (
            <section className="of-1099__section" data-section="employees">
              <SectionLabel>Employees — a W-2, not a 1099</SectionLabel>
              <p className="of-muted">{s.employees.map((e) => e.name).join(', ')}</p>
            </section>
          ) : null}
        </>
      ) : null}

      {open ? <WorkerSheet key={open.user_id} row={open} year={year} canFile={canFile} canEditTax={canEditTax} onClose={() => setOpenId(null)} /> : null}
    </div>
  );
}

/** One worker: what they were paid, what is missing, their tax details, and the 1099. */
function WorkerSheet({ row: r, year, canFile, canEditTax, onClose }) {
  const qc = useQueryClient();
  const [mode, setMode] = useState(null); // null | 'record' | 'correct' | 'dates'
  const { busy, error, run, clear } = useAction();
  const refresh = () => qc.invalidateQueries({ queryKey: ['office', '1099s', year] });
  const close = () => { clear(); setMode(null); onClose(); };
  const f = r.filed;
  const isContractor = r.tax_classification === 'contractor';

  const remove = async () => {
    if (!f || !window.confirm(`Delete the record of ${f.corrects_id ? 'this correction' : 'this 1099'}? This removes it from the app only — it does not withdraw anything from the IRS.`)) return;
    const ok = await run(() => del(`/office/1099s/filings/${f.id}`), 'Could not delete this record.');
    if (ok) { refresh(); close(); }
  };

  return (
    <Sheet open title={r.name} onClose={close}>
      <div className="of-1099__sheet" data-worker={r.user_id}>
        <FormError error={error} />
        <dl className="of-tax__figures" data-figures="paid">
          <div><dt>{`Compensation in ${year}`}</dt><dd><strong>{money(r.compensation)}</strong></dd></div>
          <div><dt>Paid (gross)</dt><dd>{money(r.gross)}</dd></div>
          <div><dt>Of which reimbursements</dt><dd>{money(r.reimbursements)}</dd></div>
          <div><dt>Net paid</dt><dd>{money(r.net)}</dd></div>
          <div><dt>Paystubs</dt><dd>{r.paystubs}</dd></div>
        </dl>
        <p className="of-muted" data-note="box1-basis">Box 1 is proposed as the compensation: the gross less the lines entered as reimbursements, because repaying a contractor&rsquo;s receipts is not pay for their work. Whether a 1099 is needed at all is tested on the same figure. If a reimbursement was not backed by a receipt, the accountant may include it — change box 1 when recording.</p>

        {r.missing.length ? (
          <section data-section="missing">
            <SectionLabel>Missing before a 1099 can be prepared</SectionLabel>
            <ul className="of-1099__missing">{r.missing.map((m) => <li key={m}>{MISSING[m] || m}</li>)}</ul>
            {r.missing.includes('tin') ? (
              <p className="of-muted">{r.tin_type === 'ein'
                ? 'An EIN cannot be entered in the app yet; keep it on the W-9 for now.'
                : 'The SSN is entered on the worker\'s profile under Crew, with the passkey.'}</p>
            ) : null}
            {r.missing.includes('address') ? <p className="of-muted">The mailing address is on the worker's profile under Crew.</p> : null}
          </section>
        ) : null}

        {canEditTax ? <TaxDetails row={r} onSaved={refresh} /> : null}
        {!canEditTax && r.missing.some((m) => ['classification', 'tin_type', 'w9'].includes(m)) ? (
          <p className="of-muted" data-note="ask-admin">An admin or the bookkeeper records whether a worker is a contractor, their SSN or EIN type, and the W-9.</p>
        ) : null}

        {f ? (
          <section data-section="filed">
            <SectionLabel>{f.corrects_id ? 'As corrected' : 'As filed'}</SectionLabel>
            <dl className="of-tax__figures" data-figures="filed">
              <div><dt>Box 1</dt><dd>{money(f.amount_cents)}</dd></div>
              <div><dt>Name on it</dt><dd>{f.recipient_name}</dd></div>
              <div><dt>TIN</dt><dd>{f.tin_type ? `${f.tin_type.toUpperCase()}${f.tin_last4 ? ` ending ${f.tin_last4}` : ''}` : '—'}</dd></div>
              <div><dt>Statement sent</dt><dd>{f.statement_sent_on ? fmtDate(f.statement_sent_on) : 'Not recorded'}</dd></div>
              <div><dt>Filed</dt><dd>{f.filed_on ? `${fmtDate(f.filed_on)}${f.confirmation ? ` · ${f.confirmation}` : ''}` : 'Not recorded'}</dd></div>
            </dl>
            {r.paid_since_filing > 0 ? (
              <Notice tone="warn" data-note="difference">{`${money(r.paid_since_filing)} was marked paid after this 1099 was recorded. A corrected 1099 may be needed.`}</Notice>
            ) : r.difference ? (
              <p className="of-muted" data-note="box1-differs">{`Box 1 is ${money(Math.abs(r.difference))} ${r.difference > 0 ? 'under' : 'over'} the compensation paid in ${year}. That is expected if box 1 was changed on purpose when it was recorded, or if it was recorded before Sep 27, 2026, when box 1 was proposed as the gross; a correction is needed only if box 1 was wrong.`}</p>
            ) : null}
          </section>
        ) : null}

        {canFile && isContractor && !mode ? (
          <div className="of-tax__actions">
            {/* On the GROSS: box 1 is editable away from the compensation, and
                the note above says to do exactly that when a reimbursement had
                no receipt — so a year of reimbursements alone must still be
                recordable (review of #720). */}
            {!f ? <FwButton onClick={() => setMode('record')} disabled={r.gross === 0} data-action="record-1099">Record 1099</FwButton> : null}
            {f && (!f.filed_on || !f.statement_sent_on) ? <FwButton variant="ghost" onClick={() => setMode('dates')} data-action="add-dates">Add dates</FwButton> : null}
            {f ? <FwButton variant="ghost" onClick={() => setMode('correct')} data-action="correct-1099">Record a correction</FwButton> : null}
            {f ? <FwButton variant="ghost" onClick={remove} disabled={busy} data-action="delete-1099">Delete this record</FwButton> : null}
          </div>
        ) : null}
        {mode === 'record' || mode === 'correct' ? (
          <FilingForm row={r} year={year} corrects={mode === 'correct' ? f : null} onDone={() => { refresh(); setMode(null); }} onCancel={() => setMode(null)} />
        ) : null}
        {mode === 'dates' ? <DatesForm filed={f} onDone={() => { refresh(); setMode(null); }} onCancel={() => setMode(null)} /> : null}
      </div>
    </Sheet>
  );
}

/** Contractor or employee, SSN or EIN, business name, W-9. Admin and accounting (financials.export). */
function TaxDetails({ row: r, onSaved }) {
  const [form, setForm] = useState({
    tax_classification: r.tax_classification || '', tin_type: r.tin_type || '', business_name: r.business_name || '', w9_received_on: r.w9_received_on || '',
  });
  const { busy, error, run } = useAction();
  const [saved, setSaved] = useState(false);
  const set = (k) => (e) => { setSaved(false); setForm((v) => ({ ...v, [k]: e.target.value })); };
  const save = async (e) => {
    e.preventDefault();
    const ok = await run(() => put(`/office/1099s/workers/${r.user_id}`, form), 'Could not save the tax details.');
    if (ok) { setSaved(true); onSaved(); }
  };
  return (
    <form id="of-1099-tax" className="or-form of-1099__form" onSubmit={save} data-form="tax-details">
      <SectionLabel>Tax details</SectionLabel>
      <FormError error={error} />
      <FormRow>
        <FwField label="Contractor or employee" htmlFor="of-1099-class" hint="Confirm with the accountant">
          <FwSelect id="of-1099-class" value={form.tax_classification} onChange={set('tax_classification')}>
            <option value="">Not decided</option>
            <option value="contractor">Contractor (1099)</option>
            <option value="employee">Employee (W-2)</option>
          </FwSelect>
        </FwField>
        <FwField label="Taxpayer ID" htmlFor="of-1099-tin">
          <FwSelect id="of-1099-tin" value={form.tin_type} onChange={set('tin_type')}>
            <option value="">Not recorded</option>
            <option value="ssn">SSN</option>
            <option value="ein">EIN (a business)</option>
          </FwSelect>
        </FwField>
      </FormRow>
      <FormRow>
        <FwField label="Business name" htmlFor="of-1099-business" hint="Line 2 of the W-9, if any"><FwInput id="of-1099-business" value={form.business_name} onChange={set('business_name')} /></FwField>
        <FwField label="W-9 received" htmlFor="of-1099-w9"><FwInput id="of-1099-w9" type="date" value={form.w9_received_on} onChange={set('w9_received_on')} /></FwField>
      </FormRow>
      <div className="of-tax__actions">
        <FwButton type="submit" variant="ghost" loading={busy} data-action="save-tax-details">Save tax details</FwButton>
        {saved ? <span className="of-muted" data-saved>Saved.</span> : null}
      </div>
    </form>
  );
}

/** Record a 1099 as sent and/or filed, or a correction. Box 1 starts as the compensation; blank for an original when that is $0 (a correction can be $0, so it keeps 0.00). */
function FilingForm({ row: r, year, corrects, onDone, onCancel }) {
  const [form, setForm] = useState({
    // Blank rather than "0.00" when the year's compensation is $0: the server
    // refuses an original 1099 for $0, and a prefilled 0.00 read as a figure
    // somebody had worked out (review of #720).
    amount: r.compensation > 0 || corrects ? (r.compensation / 100).toFixed(2) : '', statement_sent_on: corrects ? '' : todayIso(), filed_on: '', confirmation: '', tin_last4: '', notes: '',
  });
  const { busy, error, run, setError } = useAction();
  const set = (k) => (e) => setForm((v) => ({ ...v, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    const amount = dollarsToCents(form.amount);
    if (amount == null) { setError('Box 1 must be an amount, like 2400.00.'); return; }
    if (form.tin_last4 && !/^\d{4}$/.test(form.tin_last4)) { setError('Only the last four digits of the TIN — never the whole number.'); return; }
    if (!form.statement_sent_on && !form.filed_on) { setError('Enter when the contractor\'s copy went out, when it was filed, or both.'); return; }
    const body = { tax_year: year, user_id: r.user_id, amount_cents: amount };
    ['statement_sent_on', 'filed_on', 'confirmation', 'tin_last4', 'notes'].forEach((k) => { if (form[k].trim()) body[k] = form[k].trim(); });
    if (corrects) body.corrects_id = corrects.id;
    const ok = await run(() => post('/office/1099s/filings', body), 'Could not record the 1099.');
    if (ok) onDone();
  };
  return (
    <form id="of-1099-file" className="or-form of-1099__form" onSubmit={submit} data-form={corrects ? 'correct' : 'record'}>
      <SectionLabel>{corrects ? 'Record a corrected 1099' : 'Record 1099'}</SectionLabel>
      <FormError error={error} />
      <FormRow>
        <FwField label="Box 1 (nonemployee compensation)" htmlFor="of-1099-amount" required><FwInput id="of-1099-amount" inputMode="decimal" value={form.amount} onChange={set('amount')} required /></FwField>
        <FwField label="TIN, last 4 digits" htmlFor="of-1099-last4" hint="Never the whole number"><FwInput id="of-1099-last4" inputMode="numeric" maxLength={4} value={form.tin_last4} onChange={set('tin_last4')} /></FwField>
      </FormRow>
      <FormRow>
        <FwField label="Copy sent to contractor" htmlFor="of-1099-sent"><FwInput id="of-1099-sent" type="date" value={form.statement_sent_on} onChange={set('statement_sent_on')} /></FwField>
        <FwField label="Filed with the IRS" htmlFor="of-1099-filed"><FwInput id="of-1099-filed" type="date" value={form.filed_on} onChange={set('filed_on')} /></FwField>
      </FormRow>
      <FwField label="Confirmation (IRIS receipt ID)" htmlFor="of-1099-conf"><FwInput id="of-1099-conf" value={form.confirmation} onChange={set('confirmation')} /></FwField>
      <FwField label="Notes" htmlFor="of-1099-notes"><FwTextarea id="of-1099-notes" rows={2} value={form.notes} onChange={set('notes')} /></FwField>
      <SheetFooter onCancel={onCancel} busy={busy} submitLabel={corrects ? 'Record correction' : 'Record 1099'} formId="of-1099-file" />
    </form>
  );
}

/** Add the statement or filing date to a recorded 1099. */
function DatesForm({ filed, onDone, onCancel }) {
  const [form, setForm] = useState({ statement_sent_on: filed.statement_sent_on || '', filed_on: filed.filed_on || todayIso(), confirmation: filed.confirmation || '' });
  const { busy, error, run } = useAction();
  const set = (k) => (e) => setForm((v) => ({ ...v, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    const body = {};
    ['statement_sent_on', 'filed_on', 'confirmation'].forEach((k) => { if (form[k].trim()) body[k] = form[k].trim(); });
    const ok = await run(() => put(`/office/1099s/filings/${filed.id}`, body), 'Could not save the dates.');
    if (ok) onDone();
  };
  return (
    <form id="of-1099-dates" className="or-form of-1099__form" onSubmit={submit} data-form="dates">
      <SectionLabel>Add dates</SectionLabel>
      <FormError error={error} />
      <FormRow>
        <FwField label="Copy sent to contractor" htmlFor="of-1099-d-sent"><FwInput id="of-1099-d-sent" type="date" value={form.statement_sent_on} onChange={set('statement_sent_on')} /></FwField>
        <FwField label="Filed with the IRS" htmlFor="of-1099-d-filed"><FwInput id="of-1099-d-filed" type="date" value={form.filed_on} onChange={set('filed_on')} /></FwField>
      </FormRow>
      <FwField label="Confirmation (IRIS receipt ID)" htmlFor="of-1099-d-conf"><FwInput id="of-1099-d-conf" value={form.confirmation} onChange={set('confirmation')} /></FwField>
      <SheetFooter onCancel={onCancel} busy={busy} submitLabel="Save dates" formId="of-1099-dates" />
    </form>
  );
}
